// 표 위젯(InputWidget.widget==='table') 칼럼 선언(`fields[]`) 저작 로직 — 순수 함수 층.
//
// 대상은 **배치된 위젯이 소유한 칼럼 배열**이다. 같은 `DataDefField` 형상을 쓰는
// `DataDef.fields`(데이터 탭)와 배열이 다르고 서로 동기화되지 않는다(정본 보존 규약 —
// composerWidgetSync는 트리를 변형하지 않는다). 그래서 위젯 칼럼은 위젯 패널에서 고쳐야 한다.
//
// key는 여기서 바꾸지 않는다(patch에서 무시) — 위젯 칼럼 key는 `content[bindingKey]` 행 객체의
// 키라, 바꾸면 이미 작성된 값이 끊긴다. DataDef 쪽 patchField가 하는 defaultRows 마이그레이션에
// 대응하는 장치도 위젯에는 없다.
//
// 화면은 EditorPage의 TableWidgetColumnsEditor가 그린다.

import type { DataDefField, RegisteredWidgetWrite } from '@safetyhub/shared';

import { patchSuggestionsFrom } from './suggestionSources';

/** key는 저작 대상이 아니다 — 편집기에서도 읽기 전용으로 표시한다. */
export const TABLE_COLUMN_KEY_READONLY_REASON =
  'key는 작성값(행 객체)의 저장 키라 바꾸면 기존 작성값이 끊깁니다 — 칼럼 key 변경은 템플릿 마이그레이션으로 합니다.';

/**
 * 한 칼럼(key 일치)에 patch를 얹은 새 배열. 대상이 없으면 **원본 참조 그대로**(무변경 렌더 보존).
 *
 * patch의 값이 `undefined`인 키는 **제거**한다(기본값은 JSON에 남기지 않는다는 저작 규약 —
 * 빈 문자열/0으로 남으면 템플릿 diff가 지저분해지고 "지정됨"으로 오독된다).
 * `key`는 patch에 있어도 무시한다(위 파일 주석).
 */
export function patchTableColumn(
  fields: DataDefField[],
  key: string,
  patch: Partial<DataDefField>,
): DataDefField[] {
  if (!fields.some((f) => f.key === key)) return fields;
  return fields.map((f) => {
    if (f.key !== key) return f;
    // key는 patch에 있어도 버린다(위 파일 주석 — 작성값이 끊긴다).
    const rest = Object.fromEntries(Object.entries(patch).filter(([k]) => k !== 'key'));
    // 스프레드는 값이 undefined여도 키를 만들어 두므로 병합 후 한 번에 걷어낸다.
    const merged = Object.fromEntries(
      Object.entries({ ...f, ...rest }).filter(([, v]) => v !== undefined),
    );
    return merged as unknown as DataDefField;
  });
}

/**
 * 셀 입력 위젯 교체 patch — 'text'는 기본값이라 미지정으로 정규화하고, 선택형이 아니게 되면
 * options를 폐기한다(DataDefFieldRow의 종전 규약과 동일 — 혼동 방지).
 */
export function columnWidgetPatch(
  widget: NonNullable<DataDefField['widget']> | 'text',
): Partial<DataDefField> {
  const isChoice = widget === 'select' || widget === 'radio';
  return {
    widget: widget === 'text' ? undefined : widget,
    ...(isChoice ? {} : { options: undefined }),
  };
}

/**
 * 셀 정렬 select raw 값 → 저장 patch. 유효 값 외(빈 값='기본')는 **키 제거**(undefined) —
 * 미지정이 곧 "기존 렌더 그대로"라는 rc 계약이라 기본을 값으로 남기면 지정으로 오독된다.
 */
export function columnAlignPatch(raw: string): Partial<DataDefField> {
  return {
    align: raw === 'start' || raw === 'center' || raw === 'end' ? raw : undefined,
  };
}

/**
 * 접기 줄 수 raw 입력 → 저장값(1 이상만 유효, 그 외 미지정=접기 없음).
 * DataDef 칼럼 편집기의 종전 정규화와 **같은 식**이다(두 패널이 같은 형상을 고치므로 갈라두지 않는다).
 */
export function columnCollapseLinesPatch(raw: string): Partial<DataDefField> {
  const n = Number(raw);
  return {
    collapseLines: raw === '' || !Number.isFinite(n) || n < 1 ? undefined : n,
  };
}

/**
 * 칼럼의 검색 제안 축(suggestionsFrom) 부분 갱신 — 정규화·소스 교체 규약은 위젯 축과 **같은 정본**
 * (patchSuggestionsFrom)을 쓴다. 미설정이면 축 키 자체를 제거한다(빈 객체 저장 금지).
 */
export function patchTableColumnSuggestionsFrom(
  fields: DataDefField[],
  key: string,
  patch: {
    catalogKey?: string;
    filterByRowField?: string | undefined;
    writes?: RegisteredWidgetWrite[];
    requireSelection?: boolean;
  },
): DataDefField[] {
  const target = fields.find((f) => f.key === key);
  if (!target) return fields;
  const next = patchSuggestionsFrom(target.suggestionsFrom, patch);
  return patchTableColumn(fields, key, {
    suggestionsFrom: next,
    // 축을 끄면 그 축에 딸린 셀 전파도 함께 폐기 — 소스 없이 남으면 영영 발화하지 않는 잔재다.
    ...(next ? {} : { suggestionWrites: undefined }),
  });
}

/**
 * 칼럼 선택 전파 매핑(suggestionWrites) 설정 — 0건이면 키 제거(빈 배열 저장 금지).
 * target은 bindingKey가 아니라 **같은 행의 칼럼 key**다(rc composerWidgetTreeRender 셀 경로).
 */
export function setTableColumnSuggestionWrites(
  fields: DataDefField[],
  key: string,
  writes: RegisteredWidgetWrite[],
): DataDefField[] {
  return patchTableColumn(fields, key, {
    suggestionWrites: writes.length > 0 ? writes : undefined,
  });
}

/**
 * 검색 필터 칼럼 후보 — 필터 값은 **같은 행의 다른 칼럼**에서 읽는다. 자기 자신은 뺀다
 * (지금 치고 있는 칸의 값으로 그 칸의 검색을 거르는 지정은 의미가 없다).
 */
export function siblingColumnOptions(
  fields: DataDefField[],
  selfKey: string,
): { key: string; label: string }[] {
  return fields
    .filter((f) => f.key !== selfKey)
    .map((f) => ({ key: f.key, label: f.label || f.key }));
}

/**
 * 선택 전파 대상 후보 — 필터와 달리 **자기 칼럼도 포함**한다. 제안 행의 raw 필드로 자기 셀을
 * 덮어쓰는 지정이 실제로 쓰인다(SI 조문 칸: 제안 text는 법령명 머리글이 붙은 합본이라, 조문
 * 칼럼에는 raw `articleText`를 다시 써 넣는다).
 */
export function columnWriteTargetOptions(
  fields: DataDefField[],
): { key: string; label: string }[] {
  return fields.map((f) => ({ key: f.key, label: f.label || f.key }));
}
